import { CompRiskLevel, TransactionAlerts } from '@/api';
import { AlertType } from '@/models';
import { RiskKeys } from '@/config';

export const nullLevels: Record<AlertType | CompRiskLevel, number | null> = {
  low: null,
  medium: null,
  high: null,
  severe: null,
  na: null,
};

export type TransactionAlertsTableDataType = {
  key: React.Key;
  risk: AlertType;
  direction: string;
  thresholdFrom: number;
  thresholdTo: number;
};

const transactionAlertsDemoLevels = {
  atm: {
    income: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
  },
  child_exploitation: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
  },
  dark_market: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
  },
  dark_service: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
  },
  decentralized_exchange_contract: {
    income: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
  },
  enforcement_action: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 500,
        high: 750,
        severe: 1000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 500,
        high: 750,
        severe: 1000,
        na: null,
      },
    },
  },
  exchange: {
    income: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 20000,
        severe: 40000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 40000,
        severe: 100000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 20000,
        severe: 40000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 40000,
        severe: 100000,
        na: null,
      },
    },
  },
  exchange_fraudulent: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 500,
        high: 750,
        severe: 1000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 500,
        high: 750,
        severe: 1000,
        na: null,
      },
    },
  },
  gambling: {
    income: {
      direct: {
        low: 250,
        medium: 500,
        high: 1000,
        severe: 2000,
        na: null,
      },
      indirect: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 250,
        medium: 500,
        high: 1000,
        severe: 2000,
        na: null,
      },
      indirect: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
    },
  },
  illegal_service: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 500,
        severe: 750,
        na: null,
      },
    },
  },
  infrastructure_as_a_service: {
    income: {
      direct: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
      indirect: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
      indirect: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
    },
  },
  liquidity_pools: {
    income: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
  },
  malware: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  marketplace: {
    income: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 15000,
        severe: 30000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 30000,
        severe: 50000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 15000,
        severe: 30000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 30000,
        severe: 50000,
        na: null,
      },
    },
  },
  merchant_services: {
    income: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 15000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 30000,
        severe: 50000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 4000,
        medium: 7000,
        high: 15000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: 30000,
        severe: 50000,
        na: null,
      },
    },
  },
  miner: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
    },
  },
  mixer: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 200,
        medium: 400,
        high: 800,
        severe: 1500,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 200,
        medium: 400,
        high: 800,
        severe: 1500,
        na: null,
      },
    },
  },
  other: {
    income: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
  },
  p2p_exchange: {
    income: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
  },
  p2p_exchange_mlrisk_high: {
    income: {
      direct: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
      indirect: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 500,
        medium: 1000,
        high: 2000,
        severe: 4000,
        na: null,
      },
      indirect: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
    },
  },
  payment: {
    income: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
  },
  ransom: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  risky_exchange: {
    income: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
  },
  sanctions: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  scam: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  seized_assets: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: null,
        medium: null,
        high: null,
        severe: null,
        na: null,
      },
    },
  },
  stolen_coins: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  terrorism_financing: {
    income: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: null,
        medium: null,
        high: null,
        severe: 1,
        na: null,
      },
      indirect: {
        low: 100,
        medium: 250,
        high: 450,
        severe: 750,
        na: null,
      },
    },
  },
  unnamed_service: {
    income: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 1000,
        medium: 2000,
        high: 4000,
        severe: 8000,
        na: null,
      },
      indirect: {
        low: 2000,
        medium: 4000,
        high: 8000,
        severe: 15000,
        na: null,
      },
    },
  },
  wallet: {
    income: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
    outcome: {
      direct: {
        low: 2500,
        medium: 5000,
        high: 10000,
        severe: 20000,
        na: null,
      },
      indirect: {
        low: 5000,
        medium: 10000,
        high: 20000,
        severe: 40000,
        na: null,
      },
    },
  },
};

export const transactionAlertsDemoData: TransactionAlerts = {
  categories: Object.keys(transactionAlertsDemoLevels) as RiskKeys[],
  levels: transactionAlertsDemoLevels,
  directions: ['income', 'outcome'],
  interactionTypes: ['direct', 'indirect'],
};
